import { BookOpen, FileText, LockKeyhole } from 'lucide-react'

type InfoPageKind = 'help' | 'privacy' | 'terms'

type InfoPageContent = {
  icon: typeof BookOpen
  eyebrow: string
  title: string
  description: string
  sections: Array<{ title: string; items: string[] }>
}

const pageContent: Record<InfoPageKind, InfoPageContent> = {
  help: {
    icon: BookOpen,
    eyebrow: '使用帮助',
    title: '开始使用 JIAN',
    description: '从素材、项目到成片，以下是最常用的工作方式。',
    sections: [
      {
        title: '创建与编辑项目',
        items: [
          '从左侧“创建项目”开始，选择创建片段或添加字幕。',
          '在“项目”中继续已有工作；进入编辑器后可在时间线上调整素材、文字和声音。',
          'AI 创作生成的内容可继续进入项目编辑，无需重新上传素材。',
        ],
      },
      {
        title: 'AI 创作与额度',
        items: [
          'AI 配音、转写、数字人等能力会消耗套餐内的 AI 处理额度。',
          '可在“设置 → 账单与用量”查看套餐、剩余额度和已开通功能。',
          '用量达到提示阈值后，侧栏会显示升级入口。',
        ],
      },
      {
        title: 'MCP 与问题反馈',
        items: [
          '在左侧 MCP 入口或“设置 → MCP”获取当前工作区的连接信息。',
          '若生成、上传或导出失败，请保留项目名称、操作时间和错误提示后联系支持人员。',
        ],
      },
    ],
  },
  privacy: {
    icon: LockKeyhole,
    eyebrow: '隐私说明',
    title: '我们如何处理你的数据',
    description: '本说明概述产品运行所需的数据处理方式；正式隐私政策发布后将以正式版本为准。',
    sections: [
      {
        title: '我们处理的数据',
        items: [
          '账户资料：用于登录、识别账户和提供套餐权益。',
          '项目与素材：用于保存编辑内容、生成结果和跨设备继续工作。',
          '使用与设备信息：用于保障服务安全、统计额度和排查故障。',
        ],
      },
      {
        title: 'AI 处理',
        items: [
          '当你主动发起配音、转写、图片、音乐或视频生成时，必要的文本、音频或素材会被发送到云端 AI 服务完成任务。',
          '生成结果会按项目或素材库设置保存，供你继续编辑、下载或删除。',
        ],
      },
      {
        title: '你的控制权',
        items: [
          '你可以在项目与素材库中管理已创建内容。',
          '注销账户会移除账户资料与登录凭据；受订阅、订单和用量审计约束的记录会去除个人身份信息后按保留规则存档。',
          '不要将他人的敏感个人信息或无权处理的素材上传到服务。',
        ],
      },
    ],
  },
  terms: {
    icon: FileText,
    eyebrow: '服务条款',
    title: 'JIAN 服务使用条款',
    description: '使用 JIAN 前，请了解以下基础规则；正式服务条款发布后将以正式版本为准。',
    sections: [
      {
        title: '账户与安全',
        items: [
          '请使用真实、有效的账户信息，并妥善保管登录凭据。',
          '你应对账户内发起的项目、生成和导出操作负责。',
          '发现异常登录或安全问题时，请及时修改密码并联系支持人员。',
        ],
      },
      {
        title: '内容与使用限制',
        items: [
          '你应确保上传素材、脚本、声音和人物形象具有合法授权。',
          '不得使用服务生成、传播违法、侵权、欺诈或侵犯他人隐私的内容。',
          'AI 生成结果需要由你在发布前完成必要审核，并对最终发布内容负责。',
        ],
      },
      {
        title: '套餐与服务变更',
        items: [
          '套餐包含的 AI 额度和功能范围以“设置 → 账单与用量”中显示的实际权益为准。',
          '我们可能为维护安全、质量和合规要求更新功能或调整服务规则，并在产品内提供说明。',
        ],
      },
    ],
  },
}

export function InfoPageCanvas({ kind }: { kind: InfoPageKind }) {
  const content = pageContent[kind]
  const Icon = content.icon

  return (
    <div className="mx-auto w-full max-w-4xl px-5 py-10 sm:px-8 sm:py-14">
      <div className="border-b border-border pb-8">
        <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="size-5" />
        </div>
        <div className="mt-5 text-xs font-semibold tracking-[0.16em] text-primary">
          {content.eyebrow}
        </div>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {content.title}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
          {content.description}
        </p>
      </div>

      <div className="divide-y divide-border">
        {content.sections.map((section) => (
          <section key={section.title} className="py-7 sm:py-8">
            <h2 className="text-base font-semibold text-foreground">{section.title}</h2>
            <ul className="mt-3 space-y-2.5 text-sm leading-6 text-muted-foreground">
              {section.items.map((item) => (
                <li
                  key={item}
                  className="relative pl-4 before:absolute before:left-0 before:top-2.5 before:size-1 before:rounded-full before:bg-primary"
                >
                  {item}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}
